/*
 * business-card/layout.js — from a preset to drawable millimetres.
 *
 * WHY BOTH RENDERERS ARE HANDED FINISHED GEOMETRY
 * ───────────────────────────────────────────────
 * The preview is a canvas; the deliverable is a PDF. If each one decided
 * independently where a line breaks and how big the type ended up, they would
 * disagree — and the user would find out from the printer, which is the one
 * place a disagreement costs money. So wrapping, auto-shrink, bleed expansion
 * and colour resolution all happen ONCE, here, and both back ends receive the
 * same finished list. Preview and print then agree by construction.
 *
 * TEXT FITTING IS NOT REIMPLEMENTED. `LabelLayout.fitText` already does greedy
 * wrapping, per-character breaking for unbreakable strings, monotone shrink to
 * a legibility floor, and — the part that matters — it returns an explicit
 * verdict instead of silently clipping. That module is tested and shipped; a
 * second copy here would be a second set of bugs. The floors it uses
 * (2.2 mm hard, 2.6 mm "went small") land at 6.2 pt and 7.4 pt, which is
 * exactly the range card type stops being readable in, so they transfer.
 *
 * WHAT IT ADDS ON TOP is composition, which a label does not have: several
 * blocks competing for one small rectangle, where shrinking the right one
 * matters. A job title may shrink; a phone number may not. See `PRIORITY`.
 *
 * UNITS. Millimetres, origin at the TRIM top-left, y DOWN. Text `y` is the
 * BASELINE of the first line — not the top of the em box — because that is what
 * both canvas and PDF actually position from, and converting in two places is
 * how a preview ends up 0.4 mm out from the print.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CardLayout = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* Who yields when the box is too small. A card that shrinks the phone
     * number to fit a long job title has optimised the wrong thing: the title
     * is context, the number is the point of the object. Lower number = shrinks
     * first. */
    var PRIORITY = { tagline: 0, role: 1, company: 2, address: 3, name: 4, contact: 5 };

    function num(v, d) {
        var n = typeof v === 'string' ? parseFloat(v.replace(',', '.')) : v;
        return (typeof n === 'number' && isFinite(n)) ? n : d;
    }

    /* ── geometry handed to a preset ────────────────────────────────────────*/
    function geometry(boxes) {
        return {
            w: boxes.w, h: boxes.h,
            safe: boxes.safe, bleed: boxes.bleed,
            sx: boxes.safeBox.x, sy: boxes.safeBox.y,
            sw: boxes.safeBox.w, sh: boxes.safeBox.h,
        };
    }

    /* ── colour roles → real ink ────────────────────────────────────────────
     * A preset says 'accent'; the palette says what that is. Resolving here and
     * nowhere else is what lets any palette drop onto any layout.
     *
     * `onColor` inverts the pair for content sitting on an ink field: an icon
     * drawn in 'accent' over an 'ink' band would be invisible, and a preset
     * should not have to know which of its own areas are dark.
     */
    function resolveColor(role, palette, opacity) {
        var c = palette[role] || palette.ink;
        if (opacity != null && opacity < 1) {
            /* Tinting toward paper rather than using alpha: PDF transparency
             * needs an ExtGState and some RIPs flatten it unpredictably, while
             * a lighter ink is just a lighter ink. On paper they look the same;
             * in a print queue only one of them is safe. */
            var p = palette.paper;
            return {
                c: c.c * opacity + p.c * (1 - opacity),
                m: c.m * opacity + p.m * (1 - opacity),
                y: c.y * opacity + p.y * (1 - opacity),
                k: c.k * opacity + p.k * (1 - opacity),
            };
        }
        return c;
    }

    /* ── bleed expansion ────────────────────────────────────────────────────
     * A preset marks a background with the EDGES IT TOUCHES, as letters:
     * 'l', 't', 'r', 'b' in any combination, or 'all'. Growing it here means
     * the preset never mentions the bleed distance, so the same layout is
     * correct at 2 mm (RU) and 3.175 mm (US) with no edit.
     *
     * ⚠️ THE EDGE SET IS EVERY EDGE THE SHAPE TOUCHES, NOT THE ONE IT IS NAMED
     * AFTER. A colour band down the left of the card touches the left edge —
     * and also the top and the bottom, because it runs the full height. Bleeding
     * only 'left' leaves a white hairline along the top and bottom of the band
     * whenever the cut drifts outward, which is the exact defect this tool sells
     * itself on catching. Measured by our own preflight across the preset
     * matrix: 75 of 300 combinations, every single band-left, band-top and
     * split card.
     *
     * Word-style codes ('left', 'top') are deliberately NOT accepted: matching
     * them by substring makes 'left' contain a 't' and silently bleed the top
     * as well, which is the same class of bug one level down.
     */
    function expandBleed(el, bleed) {
        var s = el.bleed;
        if (!s) return { x: el.x, y: el.y, w: el.w, h: el.h };
        var all = (s === 'all' || s === true);
        var L = all || s.indexOf('l') >= 0;
        var R = all || s.indexOf('r') >= 0;
        var T = all || s.indexOf('t') >= 0;
        var B = all || s.indexOf('b') >= 0;
        return {
            x: el.x - (L ? bleed : 0),
            y: el.y - (T ? bleed : 0),
            w: el.w + (L ? bleed : 0) + (R ? bleed : 0),
            h: el.h + (T ? bleed : 0) + (B ? bleed : 0),
        };
    }

    /* ── the build ──────────────────────────────────────────────────────────
     * doc     — the card document (see index.js for its shape)
     * side    — 'front' | 'back'
     * ctx     — { measure, palette, boxes, preset, qr, logo }
     */
    function build(doc, side, ctx) {
        var boxes = ctx.boxes;
        var g = geometry(boxes);
        var palette = ctx.palette;
        var measure = ctx.measure;
        var LL = ctx.fitter;               // LabelLayout, injected

        var data = compact(doc.data);
        var opt = {
            hasLogo: !!(ctx.logo && ctx.logo.ok),
            logoRatio: (ctx.logo && ctx.logo.ratio) || 1,
            hasQr: !!(doc.qr && doc.qr.enabled),
            qrSize: (doc.qr && doc.qr.size) || 16,
            side: side,
            /* ── the measurement presets are allowed to ask for ──────────────
             * Without this a preset has to GUESS how tall a block will be —
             * one line per contact row, always — and every guess is wrong the
             * moment a 51-character email lands in a 38 mm column. It then
             * either overlaps the row below or spills past the safe zone, and
             * the layout stage discovers it too late to move anything.
             *
             * It runs the SAME `fitText` the layout stage will run, with the
             * same arguments, so what the preset positions against and what is
             * finally drawn cannot disagree. */
            fit: function (text, w, size, maxLines, weight) {
                var style = {
                    size: size, lineHeight: 1.22, maxLines: maxLines || 1,
                    weight: weight || 400, family: ctx.family,
                };
                var r = LL.fitText(String(text == null ? '' : text),
                    { w: w, h: size * 1.22 * (maxLines || 1) }, style, measure);
                return { size: r.size, lines: r.lines.length, fits: r.fits, lineHeight: r.lineHeight };
            },
        };

        var raw = side === 'back'
            ? ctx.backPreset.build(g, data, opt)
            : ctx.preset.build(g, data, opt);

        /* The QR is placed by the document, not by the preset — the user drags
         * it and expects it to stay where they put it across a preset change.
         * Backs that own a QR slot declare it themselves and are left alone. */
        if (opt.hasQr && side === doc.qr.side && !hasType(raw, 'qr')) {
            raw = raw.concat([{
                type: 'qr',
                x: num(doc.qr.x, g.sx + g.sw - opt.qrSize),
                y: num(doc.qr.y, g.sy + g.sh - opt.qrSize),
                size: opt.qrSize,
            }]);
        }

        var items = [];
        var warn = [];
        var textOrdinal = 0;
        var over = (doc.overrides && doc.overrides[side]) || {};

        for (var i = 0; i < raw.length; i++) {
            var el = raw[i];
            if (!el) continue;
            switch (el.type) {
                case 'rect':   items.push(mkRect(el)); break;
                case 'frame':  items.push(mkFrame(el)); break;
                case 'poly':   items.push(mkPoly(el)); break;
                case 'line':   items.push(mkLine(el)); break;
                case 'icon':   items.push(mkIcon(el)); break;
                case 'logo':   pushLogo(el); break;
                case 'qr':     pushQr(el); break;
                case 'text':   pushText(el); break;
            }
        }

        pushPhoto();

        return { items: items, warnings: warn, geometry: g };

        /* ── the background photograph ───────────────────────────────────────
         * Full bleed by construction: it is grown to the BLEED box, never the
         * trim, so the guillotine cannot expose a white sliver down one edge —
         * which is the single commonest way a photo card comes back from the
         * printer wrong.
         *
         * ⚠️ WHERE IT GOES IN THE LIST IS NOT "FIRST". Several presets open
         * with a full-card colour plate, and their text is coloured `paper` to
         * sit on it. Putting the photo underneath that plate hides the photo
         * completely (and looks like the upload silently failed); dropping the
         * plate instead leaves paper-white text over an arbitrary photograph.
         * So the photo lands directly ON the plate and everything the preset
         * composed stays on top of it, with its colours still making sense.
         *
         * The framing — pan and zoom — is an ordinary element override, so
         * dragging the photo, the arrow keys, undo and "вернуть как было" all
         * work through the machinery every other element already uses.
         */
        function pushPhoto() {
            var bg = (doc.bg && doc.bg[side]) || null;
            if (!bg || !bg.src || !ctx.photo || !ctx.photo.ready(bg.src)) return;
            var b = g.bleed;
            var ob = over['bg'] || {};
            var box = { x: -b, y: -b, w: g.w + 2 * b, h: g.h + 2 * b };
            var it = {
                id: 'bg', pick: true, type: 'photo',
                x: box.x, y: box.y, w: box.w, h: box.h,
                src: bg.src,
                spec: {
                    src: bg.src, fit: bg.fit, blur: bg.blur,
                    light: bg.light, sat: bg.sat, tint: bg.tint,
                    rot: bg.rot, flip: bg.flip,
                },
                place: {
                    w: box.w, h: box.h,
                    zoom: num(ob.scale, 1), dx: num(ob.dx, 0), dy: num(ob.dy, 0),
                },
                colours: {
                    paper: ctx.photo.rgb(palette.paper),
                    ink: ctx.photo.rgb(palette.ink),
                    tint: ctx.photo.rgb(palette.accent),
                },
            };
            /* `dpi` is the effective print resolution AT THE PLACED SIZE, not
             * the file's own pixel count: a 4000 px photograph zoomed to 3× on
             * a 90 mm card is showing a third of itself at 90 mm, and that
             * third is what has to hold up. `pkey` names one set of baked
             * pixels, so the preview's cache and the exporter's embedded object
             * cannot be for different bakes.
             *
             * Both come from the host rather than from a global reached for
             * here: this module is handed its collaborators (`ctx.fitter`,
             * `ctx.measure`), and it runs in node under the unit tests where a
             * `window` is a lie waiting to be told. */
            var meta = ctx.photo.meta(it.spec, it.place, it.colours);
            it.dpi = meta.dpi;
            it.pkey = meta.pkey;
            var at = 0;
            while (at < items.length && isFullPlate(items[at])) at++;
            items.splice(at, 0, it);
        }
        function isFullPlate(it) {
            if (!it || it.type !== 'rect') return false;
            var b = g.bleed, e = 0.02;
            return it.x <= -b + e && it.y <= -b + e
                && it.w >= g.w + 2 * b - e && it.h >= g.h + 2 * b - e;
        }

        /* ── identity ────────────────────────────────────────────────────────
         * A user who drags the name 2 mm left expects it to STAY there when
         * they try the next palette — but not to survive a change of layout
         * that has no name block in the same role. That needs a stable name for
         * each element which is a property of its ROLE, not of its position in
         * the array: `t:name` rather than `item 4`.
         *
         * The field is recovered by identity against the data rather than by
         * tagging all forty text() calls in the presets. Comparison is on the
         * RAW value, before `upper` transforms it, so an upper-cased company
         * kicker still resolves to `company`. Two fields holding the same
         * string collide and the first wins — acceptable, and the canonical
         * order below puts the ones people actually move first.
         */
        function fieldOf(value) {
            var order = ['name', 'company', 'role', 'tagline', 'address',
                         'phone', 'phone2', 'email', 'site', 'telegram', 'whatsapp'];
            for (var k = 0; k < order.length; k++) {
                if (data[order[k]] != null && data[order[k]] === value) return order[k];
            }
            return null;
        }

        /* Apply the user's per-element adjustments. Offsets are millimetres so
         * they mean the same thing on a 90×50 card and an 85×55 one; a scale
         * would silently change meaning with the format. */
        function applyOver(id, box) {
            var o = over[id];
            if (!o) return box;
            if (typeof o.dx === 'number') box.x += o.dx;
            if (typeof o.dy === 'number') box.y += o.dy;
            return box;
        }

        /* ── per-type resolution ─────────────────────────────────────────── */

        function mkRect(el) {
            var b = expandBleed(el, g.bleed);
            return {
                type: 'rect', x: b.x, y: b.y, w: b.w, h: b.h,
                color: resolveColor(el.color, palette, el.opacity),
                radius: num(el.radius, 0),
            };
        }
        function mkFrame(el) {
            return {
                type: 'frame', x: el.x, y: el.y, w: el.w, h: el.h,
                color: resolveColor(el.color, palette, el.opacity),
                stroke: num(el.stroke, 0.3), radius: num(el.radius, 0),
            };
        }
        function mkPoly(el) {
            var b = g.bleed;
            var s = String(el.bleed || '');
            var allSides = (s === 'all');
            var L = allSides || s.indexOf('l') >= 0, R = allSides || s.indexOf('r') >= 0;
            var T = allSides || s.indexOf('t') >= 0, B = allSides || s.indexOf('b') >= 0;
            /* Push the vertices that sit ON a bleeding edge outward, rather than
             * translating the whole shape. Translating a corner wedge moves its
             * visible hypotenuse too — the design shifts by 2 mm — while only
             * the vertices touching the paper edge actually need to grow. */
            var xs = el.points.map(function (p) { return p[0]; });
            var ys = el.points.map(function (p) { return p[1]; });
            var minX = Math.min.apply(null, xs), maxX = Math.max.apply(null, xs);
            var minY = Math.min.apply(null, ys), maxY = Math.max.apply(null, ys);
            return {
                type: 'poly',
                points: el.points.map(function (p) {
                    var x = p[0], y = p[1];
                    if (L && Math.abs(x - minX) < 0.01) x -= b;
                    if (R && Math.abs(x - maxX) < 0.01) x += b;
                    if (T && Math.abs(y - minY) < 0.01) y -= b;
                    if (B && Math.abs(y - maxY) < 0.01) y += b;
                    return [x, y];
                }),
                color: resolveColor(el.color, palette, el.opacity),
            };
        }
        function mkLine(el) {
            return {
                type: 'line', x: el.x, y: el.y, w: el.w, h: num(el.h, 0),
                color: resolveColor(el.color, palette, el.opacity),
                stroke: num(el.stroke, 0.25),
            };
        }
        function mkIcon(el) {
            return {
                type: 'icon', path: el.path, x: el.x, y: el.y, size: el.size,
                color: resolveColor(el.color, palette, el.opacity),
                /* Millimetres, so preflight's hairline check grades it in the
                 * same unit as every other stroke on the card. */
                stroke: num(el.stroke, el.size * 0.07),
            };
        }

        function pushLogo(el) {
            if (!opt.hasLogo) return;
            var h = num(el.h, 10);
            var w = h * opt.logoRatio;
            /* A wide logo asked to be 10 mm tall can be 60 mm wide and run off
             * the card. Constrain by the box it was given, not by the height
             * alone — the preset knows how much room there is, the logo does
             * not. */
            var maxW = el.maxW || (g.sw);
            if (w > maxW) { w = maxW; h = w / opt.logoRatio; }
            var lo = over['logo'] || {};
            if (typeof lo.scale === 'number' && lo.scale > 0) {
                var kw = w * lo.scale, kh = h * lo.scale;
                /* Resizing keeps the anchor the preset chose: a centred mark
                 * grows about its centre, a right-aligned one about its right
                 * edge. Growing everything from the top-left would slide a
                 * centred logo off-centre the moment it is touched. */
                w = kw; h = kh;
            }
            var x = el.align === 'center' ? el.x - w / 2 : (el.align === 'right' ? el.x - w : el.x);
            var y = el.vcenter ? el.y - h / 2 : el.y;
            var lbox = applyOver('logo', { x: x, y: y });
            x = lbox.x; y = lbox.y;
            items.push({
                id: 'logo', pick: true,
                type: 'image', x: x, y: y, w: w, h: h,
                src: ctx.logo.src, onColor: !!el.onColor,
                /* One-colour mode is the only way to recolour a raster, and the
                 * back of a card is usually a dark panel — a dark logo dropped
                 * onto it is invisible. So the ink follows the surface: paper
                 * when the mark sits on a colour field, ink when it sits on
                 * paper. In full-colour mode the bitmap is whatever it is, and
                 * preflight says so rather than the layout pretending. */
                mono: !!ctx.logo.mono,
                color: resolveColor(el.onColor ? 'paper' : 'ink', palette),
                /* Effective resolution the raster will actually be printed at.
                 * Computed here because this is the only place that knows both
                 * the pixel count and the final physical size. */
                dpi: ctx.logo.natW ? Math.round(ctx.logo.natW / (w / 25.4)) : 0,
                vector: !!ctx.logo.vector,
                /* How far this logo's own colours move on press, measured by
                 * the same model the preview and the export both use. */
                shift: ctx.logo.shift || 0,
            });
        }

        function pushQr(el) {
            if (!opt.hasQr && side !== 'back') return;
            if (!ctx.qr) return;
            var size = num(el.size, opt.qrSize);
            var qbox = applyOver('qr', { x: el.x, y: el.y });
            items.push({
                id: 'qr', pick: true,
                type: 'qr', x: qbox.x, y: qbox.y, size: size,
                payload: ctx.qr.payload, ecc: ctx.qr.plan.ecc,
                plan: ctx.qr.plan,
                dark: resolveColor(el.color || 'ink', palette),
                light: palette.paper,
            });
        }

        function pushText(el) {
            var raw0 = el.text;

            var field = el.field || fieldOf(raw0);
            /* ── a stable name for every text block ──────────────────────────
             * `t:name` when the string came from a data field; `t#monogram`
             * when the preset named the block itself; `t#3` only as a last
             * resort. The ordinal is positional, so an override attached to one
             * survives a layout change by landing on whatever text happens to
             * be third in the next preset — which is why anything a user can
             * reasonably want to edit gets a `role` in presets.js instead. */
            var id = field ? ('t:' + field)
                : (el.role ? ('t#' + el.role) : ('t#' + (textOrdinal++)));
            var ov = over[id] || {};

            /* ── text the user typed over a computed block ───────────────────
             * The monogram, and anything else a preset composes rather than
             * copies from a field, has no data field to write back to. Before
             * this it was simply not editable: double-clicking the initials did
             * nothing, no message, no cursor. The replacement lives on the
             * ELEMENT as an override, next to its nudge and its size, so
             * "вернуть как было" restores the computed value. */
            if (typeof ov.text === 'string') raw0 = ov.text;

            var value = String(raw0 == null ? '' : raw0);
            if (el.upper) value = value.toUpperCase();
            if (!value.trim()) return;

            /* Per-element style the user set on the selection. `size` is a
             * MULTIPLIER, not a millimetre value: the preset owns the type
             * scale and the relationship between the blocks, and storing an
             * absolute size would freeze this block while every other one still
             * responds to the format and the global scale. */
            var elSize = el.size * (typeof ov.size === 'number' ? ov.size : 1);
            var elWeight = (typeof ov.weight === 'number') ? ov.weight : el.weight;
            var elAlign = ov.align || el.align || 'left';
            var elColor = ov.color || el.color;
            var elOpacity = ov.color ? 1 : el.opacity;

            var style = {
                size: elSize, lineHeight: el.lineHeight, maxLines: el.maxLines,
                weight: elWeight, family: el.family || ctx.family,
                track: el.track, upper: el.upper,
                mode: el.shrinkOnly === false ? 'fixed' : 'shrink',
                minSize: el.minSize,
            };
            var boxH = el.maxLines ? elSize * (el.lineHeight || 1.22) * el.maxLines : g.sh;
            var fit = LL.fitText(value, { w: el.w, h: boxH }, style, measure);

            if (!fit.fits) {
                warn.push({ code: 'overflow', field: field, id: id, text: value, side: side });
            } else if (fit.shrunk && fit.size < LL.LEGIBLE_MM) {
                warn.push({ code: 'tiny_text', field: field, id: id, size: fit.size, side: side });
            }

            var color = resolveColor(elColor, palette, elOpacity);
            /* Three ways a preset can express where text goes, all resolved to
             * the FIRST BASELINE here so no downstream renderer has to ask which
             * convention was used:
             *
             *   anchor:'bottom' — `y` is where the LAST line's ink ends.
             *   baseline:true   — `y` is the first baseline.
             *   neither         — `y` is the top of the em box; the ascent is
             *                     0.78 em, where a humanist sans puts it.
             *
             * ⚠️ 'bottom' cannot be resolved by the preset, because it depends
             * on how many lines the text turned out to need, and only the fit
             * above knows that. A preset that anchors a bottom block on its
             * FIRST baseline — the obvious reading — grows every extra line
             * DOWNWARD off the card: measured here, a two-line contact summary
             * placed at the safe bottom (46 mm) ended at 49.9 mm on a 50 mm
             * card, i.e. 0.1 mm from being guillotined away. */
            var y0;
            if (el.anchor === 'bottom') {
                y0 = el.y - fit.lineHeight * (fit.lines.length - 1) - fit.size * 0.26;
            } else if (el.baseline) {
                y0 = el.y;
            } else {
                y0 = el.y + fit.size * 0.78;
            }
            var tbox = applyOver(id, { x: el.x, y: y0 });
            items.push({
                id: id, pick: true,
                type: 'text',
                x: tbox.x, y: tbox.y, w: el.w,
                align: elAlign,
                lines: fit.lines,
                size: fit.size,
                lineHeight: fit.lineHeight,
                weight: elWeight || 400,
                track: num(el.track, 0),
                color: color,
                field: field,
                role: el.role || null,
                /* What the inline editor should open with, and what it writes
                 * back to when there is no field behind the block. `upper` is
                 * deliberately NOT applied: the editor shows what is stored, so
                 * a small-caps kicker does not shout at whoever edits it. */
                raw: String(raw0 == null ? '' : raw0),
                editable: true,
                shrunk: fit.shrunk,
            });
        }

        function hasType(list, t) {
            for (var i = 0; i < list.length; i++) if (list[i] && list[i].type === t) return true;
            return false;
        }
    }

    /* Drop empty fields so a preset is never handed a slot it should position
     * and then leave blank. This is the mechanism behind "no hole where the
     * logo was" — the preset simply never sees the field. */
    function compact(data) {
        var out = {};
        for (var k in data) {
            if (!Object.prototype.hasOwnProperty.call(data, k)) continue;
            var v = data[k];
            if (v == null) continue;
            if (typeof v === 'string' && !v.trim()) continue;
            out[k] = typeof v === 'string' ? v.trim() : v;
        }
        return out;
    }

    return {
        PRIORITY: PRIORITY,
        geometry: geometry,
        resolveColor: resolveColor,
        expandBleed: expandBleed,
        compact: compact,
        build: build,
    };
});
